import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import { getApplicationError } from '@oysterjs/core/graphql/errors';
import { LoaderFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { queryData } from '~/api/data/request';
import { loadQuery } from '~/api/data/responses';
import {
  GetKnowledgeBaseDocument,
  GetKnowledgeBaseDocumentDocument,
  GetKnowledgeBaseDocumentQuery,
  GetKnowledgeBaseDocumentQueryVariables,
  GetKnowledgeBaseQuery,
  GetKnowledgeBaseQueryVariables,
  GetKnowledgeBasesDocument,
  GetKnowledgeBasesQuery,
  GetKnowledgeBasesQueryVariables,
  KnowledgeBaseDetailFieldsFragment,
  KnowledgeBaseDocumentFieldsFragment,
  KnowledgeBaseListFieldsFragment
} from '~/api/graphql';

export const getKnowledgeBases = async () =>
  queryData<
    GetKnowledgeBasesQuery,
    GetKnowledgeBasesQueryVariables,
    KnowledgeBaseListFieldsFragment[]
  >(client, { query: GetKnowledgeBasesDocument }, (data) => data?.knowledgeBases || []);

export const getKnowledgeBase = async (knowledgeBaseId: string) =>
  queryData<
    GetKnowledgeBaseQuery,
    GetKnowledgeBaseQueryVariables,
    KnowledgeBaseDetailFieldsFragment
  >(
    client,
    {
      query: GetKnowledgeBaseDocument,
      variables: { id: knowledgeBaseId }
    },
    (data) => data?.knowledgeBase
  );

export const getDefaultKnowledgeBase = async () => {
  const result = await getKnowledgeBases();
  if (
    result.error &&
    getApplicationError(result.error)?.code !== ApplicationErrorCode.Unauthorized
  ) {
    return { data: undefined, error: result.error };
  }
  const knowledgeBases = result.data;
  return queryData<
    GetKnowledgeBaseQuery,
    GetKnowledgeBaseQueryVariables,
    KnowledgeBaseDetailFieldsFragment
  >(
    client,
    {
      query: GetKnowledgeBaseDocument,
      variables: { id: knowledgeBases?.[0]?.id || '' }
    },
    (data) => data?.knowledgeBase
  );
};

export const getKnowledgeBaseDocument = async (documentId: string) =>
  queryData<
    GetKnowledgeBaseDocumentQuery,
    GetKnowledgeBaseDocumentQueryVariables,
    KnowledgeBaseDocumentFieldsFragment
  >(
    client,
    { query: GetKnowledgeBaseDocumentDocument, variables: { id: documentId } },
    (data) => data?.knowledgeBaseDocument
  );

export const loadKnowledgeBases: LoaderFunction = () =>
  loadQuery(getKnowledgeBases(), { suppressAuthErrors: true });

export const loadKnowledgeBase: LoaderFunction = ({ params }) =>
  loadQuery(getKnowledgeBase(params.knowledgeBaseId || ''));

export const loadKnowledgeBaseDocument: LoaderFunction = ({ params }) =>
  loadQuery(getKnowledgeBaseDocument(params.documentId || ''));
